import React, { useEffect, useState } from "react";
import { fetchExhibitors, createExhibitor, updateExhibitor, deleteExhibitor } from "@/lib/adminApi";
import { PageHeader, StatCard, Chip } from "./_shared";
import { toast } from "sonner";
import { Plus, Trash2, X, Building2 } from "lucide-react";

const STATUS = ["prospect", "engaged", "booked", "paid", "lost"];
const TONE = { prospect: "slate", engaged: "sky", booked: "amber", paid: "lime", lost: "rose" };

const empty = {
  company: "", category: "", booth_number: "", contact_name: "",
  email: "", phone: "", website: "", logo_url: "",
  package: "", deal_value: 0, status: "prospect", notes: "", assigned_to: "",
};

export default function ExhibitorsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchExhibitors().then((d) => { setRows(d); setLoading(false); }); }, []);

  const openNew = () => { setEdit(null); setForm(empty); setShowForm(true); };
  const openEdit = (r) => { setEdit(r); setForm({ ...empty, ...r }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.company) { toast.error("Company required"); return; }
    setSaving(true);
    try {
      if (edit) {
        const u = await updateExhibitor(edit.id, form);
        setRows((prev) => prev.map((x) => x.id === u.id ? u : x));
        toast.success("Updated");
      } else {
        const u = await createExhibitor(form);
        setRows((prev) => [u, ...prev]);
        toast.success("Added");
      }
      setShowForm(false);
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete exhibitor ${r.company}?`)) return;
    try {
      await deleteExhibitor(r.id);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  const changeStatus = async (r, status) => {
    try {
      const u = await updateExhibitor(r.id, { status });
      setRows((prev) => prev.map((x) => x.id === u.id ? u : x));
    } catch { toast.error("Update failed"); }
  };

  const totalDeal = rows.filter(r => r.status === "paid" || r.status === "booked").reduce((s, r) => s + (r.deal_value || 0), 0);
  const inp = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-lime-500";
  const lbl = "block text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-1";

  return (
    <div data-testid="page-exhibitors">
      <PageHeader
        title="Exhibitors"
        subtitle="Manage exhibitor pipeline — from prospect to booked booth."
        actions={
          <button data-testid="ex-new" onClick={openNew} className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">
            <Plus className="h-4 w-4" /> Add exhibitor
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {STATUS.map((s) => (
          <StatCard key={s} tone={TONE[s]} label={s} value={rows.filter(r => r.status === s).length} />
        ))}
      </div>
      <div className="mb-6">
        <StatCard tone="navy" label="Committed deal value" value={"₹" + totalDeal.toLocaleString("en-IN")} sub="booked + paid" />
      </div>

      {loading ? <div className="text-sm text-slate-500">Loading…</div> : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Company</th>
                <th className="px-4 py-3 text-left font-semibold">Category</th>
                <th className="px-4 py-3 text-left font-semibold">Contact</th>
                <th className="px-4 py-3 text-left font-semibold">Booth</th>
                <th className="px-4 py-3 text-left font-semibold">Deal</th>
                <th className="px-4 py-3 text-left font-semibold">Owner</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-sm text-slate-400">No exhibitors yet. Click &quot;Add exhibitor&quot; to start.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-lime-50/40 cursor-pointer" onClick={() => openEdit(r)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {r.logo_url ? <img src={r.logo_url} alt="" className="h-7 w-7 rounded object-cover border border-slate-200" /> : <div className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center"><Building2 className="h-3.5 w-3.5 text-slate-400" /></div>}
                      <div>
                        <div className="font-semibold text-slate-900">{r.company}</div>
                        {r.website && <a href={r.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-slate-500 hover:text-lime-600">{r.website.replace(/https?:\/\//, "")}</a>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.category || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="text-slate-900">{r.contact_name || "—"}</div>
                    <div className="text-xs text-slate-500">{r.email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.booth_number || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="text-slate-900 font-semibold">{r.deal_value ? "₹" + Number(r.deal_value).toLocaleString("en-IN") : "—"}</div>
                    <div className="text-xs text-slate-500">{r.package || ""}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{r.assigned_to || <span className="text-slate-400">Unassigned</span>}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select value={r.status} onChange={(e) => changeStatus(r, e.target.value)} className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider outline-none">
                      {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => remove(r)} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Slide-over form */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setShowForm(false)} />
          <form onSubmit={save} className="relative w-full max-w-lg bg-white shadow-2xl border-l border-slate-200 flex flex-col">
            <div className="border-b border-slate-100 px-5 py-4 flex items-center justify-between">
              <div className="font-display text-lg font-bold text-slate-900">{edit ? "Edit exhibitor" : "New exhibitor"}</div>
              <button type="button" onClick={() => setShowForm(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5"/></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <div><label className={lbl}>Company *</label><input required data-testid="ex-company" className={inp} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Category</label><input className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
                <div><label className={lbl}>Booth #</label><input className={inp} value={form.booth_number} onChange={(e) => setForm({ ...form, booth_number: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Contact name</label><input className={inp} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
                <div><label className={lbl}>Email</label><input type="email" className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Phone</label><input className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><label className={lbl}>Website</label><input className={inp} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
              </div>
              <div><label className={lbl}>Logo URL</label><input className={inp} value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Package</label><input className={inp} value={form.package} onChange={(e) => setForm({ ...form, package: e.target.value })} placeholder="Platinum / Gold / …" /></div>
                <div><label className={lbl}>Deal value (INR)</label><input type="number" className={inp} value={form.deal_value} onChange={(e) => setForm({ ...form, deal_value: Number(e.target.value) })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Status</label>
                  <select className={inp} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div><label className={lbl}>Assigned to</label><input className={inp} value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} /></div>
              </div>
              <div><label className={lbl}>Notes</label><textarea rows={3} className={inp} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <div className="border-t border-slate-100 p-3 flex gap-2">
              <button data-testid="ex-save" disabled={saving} className="rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">{edit ? "Save changes" : "Add exhibitor"}</button>
              <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
